// Pure, dependency-free logic for the controlled INTERACTION component the React Session view
// renders from a durable CUSTOM interaction event (foundation slice 10). vesta-repo has NO JS test
// runner, so the client logic lives HERE as a pure module asserted by a runnable node script
// (interactionComponent.node-test.js); the visual render is a morning check.
//
// What this module decides (the two slice-10 client rules):
//   * AC#4 — READ-ONLY ON REPLAY: an interaction whose resolution already exists in the durable
//     stream (a TOOL_CALL_RESULT correlated by interaction_id) is rendered READ-ONLY. A live,
//     unresolved interaction is rendered INTERACTIVE (the human can submit). So re-opening a
//     finished Session shows the approval card as a settled record, never a live actionable form.
//   * AC#3 (client half) — the render directive must be a NAMESPACED + VERSIONED known type with a
//     valid envelope; an unknown/mismatched directive renders an inert "unsupported" placeholder
//     rather than a broken control. (The authoritative payload validation is server-side; this is
//     the client's defensive gate so a forward-version directive never throws in render.)
//
// The submit body the interactive component POSTs is also built here (so it is unit-assertable):
// { interaction_id, capability, payload } — capability is echoed from the directive, never minted.

// The render types this client build knows how to render, and the version it supports.
export const SUPPORTED_RENDERS = {
  "agent.approval": 1,
  "agent.confirm": 1,
};

// Is the directive a known namespaced type at a version this client can render?
export function isSupportedRender(render) {
  if (!render || typeof render !== "object") return false;
  const supported = SUPPORTED_RENDERS[render.name];
  return supported !== undefined && render.version === supported;
}

// Find the interaction directive(s) + their resolution status from a durable event stream.
// Returns { interaction_id, prompt, render, requiresCapability, capability, resolved } per CUSTOM
// interaction event, where `resolved` is true iff a TOOL_CALL_RESULT for that interaction exists.
export function reduceInteractions(stream) {
  const events = Array.isArray(stream) ? stream : [];
  // Which interaction_ids have a durable resolution (a TOOL_CALL_RESULT carrying it)?
  const resolvedIds = new Set();
  for (const ev of events) {
    if (ev && ev.type === "TOOL_CALL_RESULT" && ev.payload && ev.payload.interaction_id) {
      resolvedIds.add(ev.payload.interaction_id);
    }
  }

  const byId = {};
  for (const ev of events) {
    if (!ev || ev.type !== "CUSTOM") continue;
    const p = ev.payload || {};
    if (p.name !== "interaction" || !p.interaction_id) continue;
    // Idempotent on interaction_id: a replayed/duplicated CUSTOM frame settles to the same record.
    byId[p.interaction_id] = {
      interaction_id: p.interaction_id,
      prompt: p.prompt || "",
      render: p.render || {},
      requiresCapability: p.requires_capability === true,
      capability: (p.capability && p.capability.token) || null,
      capabilityAction: (p.capability && p.capability.action) || null,
      resolved: resolvedIds.has(p.interaction_id),
    };
  }
  return Object.values(byId);
}

// The view-model the React component renders from one interaction record. THIS is the read-only
// gate: `mode` is "readonly" once the interaction is resolved (replay), else "interactive".
export function interactionViewModel(interaction) {
  const supported = isSupportedRender(interaction.render);
  const resolved = interaction.resolved === true;
  return {
    interaction_id: interaction.interaction_id,
    prompt: interaction.prompt,
    render: interaction.render,
    supported,
    // Unsupported directive → inert placeholder; resolved → readonly; else interactive.
    mode: !supported ? "unsupported" : resolved ? "readonly" : "interactive",
    // The submit control is enabled ONLY for a live, supported, capability-satisfied interaction.
    canSubmit: supported && !resolved && (!interaction.requiresCapability || Boolean(interaction.capability)),
    requiresCapability: interaction.requiresCapability,
  };
}

// Build the POST body the interactive component submits. The capability is ECHOED from the
// directive Rails issued — the client never mints it. Returns null when submit is not permitted
// (so the caller cannot accidentally POST a read-only/replayed interaction).
export function buildSubmitBody(interaction, payload) {
  const vm = interactionViewModel(interaction);
  if (!vm.canSubmit) return null;
  return {
    interaction_id: interaction.interaction_id,
    capability: interaction.capability || null,
    payload: payload || {},
  };
}

// P4 — the BOARD-surface submit body. The board (SessionDrawer) approves a still-blocked money/send
// card directly off the per-viewer capability the board serializer MINTED for it
// (board_controller#capability_for_card → ViewerCapability.mint_for, bound approver only). That blob
// is `{ action, token, interaction_id }`. Unlike `buildSubmitBody` there is no durable interaction
// RECORD here (the board card is not a replayed AG-UI stream), so this builds straight off the
// capability blob. Returns null when the capability is absent (a report/done card with no minted
// token CANNOT submit — preserves the read-only contract for non-bound, non-blocked cards). The token
// is ECHOED, never minted client-side; interaction_id rides alongside (the signed token is opaque).
export function buildBoardSubmitBody(capability, payload) {
  if (!capability || !capability.token || !capability.interaction_id) return null;
  return {
    interaction_id: capability.interaction_id,
    capability: capability.token,
    payload: payload || {},
  };
}
